import { useDriveSources, useMemberExternalAccounts, useXeroConnection } from '@/hooks';
import { useMcpServerConnections } from '@/hooks/useMcpServerConnectionQueries';
import type {
  DriveSourceSummary,
  McpServerConnectionSafe,
  MemberExternalAccountSafe,
  XeroConnectionStateResult,
} from '@vivport/backend/types';
import { useMemo } from 'react';
import { SOURCE_KIND_META } from '../drives/source-kind-meta';
import { INTEGRATION_KINDS, IntegrationKind, IntegrationScope, getIntegrationKind, integrationHref } from './integration-kinds';

export type IntegrationStatusTone = 'ok' | 'attention' | 'error' | 'muted';

export const STATUS_BADGE_VARIANT: Record<IntegrationStatusTone, 'default' | 'outline' | 'destructive' | 'secondary'> = {
  ok: 'default',
  attention: 'outline',
  error: 'destructive',
  muted: 'secondary',
};

export type IntegrationRow = {
  key: string;
  kind: IntegrationKind;
  id: number;
  name: string;
  detail: string;
  scope: IntegrationScope;
  status: { label: string; tone: IntegrationStatusTone };
  href: string;
};

function row(kind: IntegrationKind, id: number, name: string, detail: string, status: IntegrationRow['status']): IntegrationRow {
  return {
    key: `${kind}:${id}`,
    kind,
    id,
    name,
    detail,
    scope: getIntegrationKind(kind).scope,
    status,
    href: integrationHref(kind, id),
  };
}

const ACCOUNT_KIND: Record<MemberExternalAccountSafe['provider'], IntegrationKind> = {
  GOOGLE: 'google-account',
  MICROSOFT: 'microsoft-account',
};

const ACCOUNT_DETAIL: Record<MemberExternalAccountSafe['provider'], string> = {
  GOOGLE: 'Gmail & Google Calendar',
  MICROSOFT: 'Outlook mail & calendar',
};

export function rowsFromMemberExternalAccounts(items: MemberExternalAccountSafe[]): IntegrationRow[] {
  return items.map((account) => {
    const lastSynced = account.messageSync.lastCompletedAt;
    const detail = lastSynced
      ? `${ACCOUNT_DETAIL[account.provider]} · last synced ${new Date(lastSynced).toLocaleString()}`
      : ACCOUNT_DETAIL[account.provider];
    const status: IntegrationRow['status'] = !account.isActive
      ? { label: 'Inactive', tone: 'muted' }
      : account.messageSync.needsReauth
        ? { label: 'Reconnect needed', tone: 'error' }
        : { label: 'Active', tone: 'ok' };
    return row(ACCOUNT_KIND[account.provider], account.id, account.externalEmail, detail, status);
  });
}

function driveSourceStatus(source: DriveSourceSummary): IntegrationRow['status'] {
  const isRelay = source.kind === 'RELAY';
  if (source.status === 'PENDING') {
    return { label: isRelay ? 'Waiting to pair' : 'Pending', tone: 'attention' };
  }
  if (source.status === 'REVOKED') {
    return { label: 'Revoked', tone: 'error' };
  }
  if (source.online) {
    return { label: isRelay ? 'Online' : 'Active', tone: 'ok' };
  }
  return isRelay ? { label: 'Offline', tone: 'error' } : { label: 'Active', tone: 'ok' };
}

export function rowsFromDriveSources(sources: DriveSourceSummary[]): IntegrationRow[] {
  return sources
    .filter((source) => !(source.kind === 'RELAY' && source.status === 'REVOKED'))
    .map((source) => {
      const meta = SOURCE_KIND_META[source.kind];
      const detail = `${meta.label} · ${source.driveCount} drive${source.driveCount === 1 ? '' : 's'}`;
      return row(meta.kind, source.id, source.name, detail, driveSourceStatus(source));
    });
}

export function rowsFromXeroConnection(state: XeroConnectionStateResult | undefined): IntegrationRow[] {
  const connection = state?.connection;
  if (!connection) {
    return [];
  }
  const lastVerified = connection.lastVerifiedAt ? new Date(connection.lastVerifiedAt).toLocaleString() : '-';
  const detail = `Connected by ${connection.connectedByMemberEmail ?? 'unknown'} · last verified ${lastVerified}`;
  const status: IntegrationRow['status'] =
    connection.status === 'PENDING_TENANT_SELECTION'
      ? { label: 'Choose organisation', tone: 'attention' }
      : connection.status === 'ACTIVE'
        ? { label: 'Active', tone: 'ok' }
        : { label: 'Revoked', tone: 'error' };
  return [row('xero', connection.id, connection.xeroTenantName ?? 'Xero', detail, status)];
}

export function rowsFromMcpConnections(connections: McpServerConnectionSafe[]): IntegrationRow[] {
  return connections.map((connection) => {
    const toolCount = connection.pinnedTools.length;
    const status: IntegrationRow['status'] = !connection.enabled
      ? { label: 'Disabled', tone: 'muted' }
      : connection.drift
        ? { label: 'Drift detected', tone: 'attention' }
        : toolCount === 0
          ? { label: 'No tools pinned', tone: 'attention' }
          : { label: `${toolCount} tool${toolCount === 1 ? '' : 's'}`, tone: 'ok' };
    return row('mcp', connection.id, connection.name, connection.url, status);
  });
}

const SCOPE_ORDER: Record<IntegrationScope, number> = { member: 0, hub: 1 };

function kindOrder(kind: IntegrationKind): number {
  return INTEGRATION_KINDS.findIndex((entry) => entry.kind === kind);
}

function compareRows(left: IntegrationRow, right: IntegrationRow): number {
  return (
    SCOPE_ORDER[left.scope] - SCOPE_ORDER[right.scope] ||
    kindOrder(left.kind) - kindOrder(right.kind) ||
    left.name.localeCompare(right.name)
  );
}

export function useIntegrationRows(memberId: number) {
  const accounts = useMemberExternalAccounts(memberId);
  const driveSources = useDriveSources(memberId);
  const xero = useXeroConnection(memberId);
  const mcp = useMcpServerConnections(memberId > 0);
  const queries = [accounts, driveSources, xero, mcp];

  const rows = useMemo(
    () =>
      [
        ...rowsFromMemberExternalAccounts(accounts.data?.items ?? []),
        ...rowsFromDriveSources(driveSources.data?.sources ?? []),
        ...rowsFromXeroConnection(xero.data),
        ...rowsFromMcpConnections(mcp.data?.connections ?? []),
      ].sort(compareRows),
    [accounts.data, driveSources.data, xero.data, mcp.data]
  );

  return {
    rows,
    isLoading: queries.some((query) => query.isLoading),
    isError: queries.some((query) => query.isError),
    error: queries.find((query) => query.error)?.error ?? null,
  };
}
